import React, { useMemo, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  MessageSquare,
  Users,
  Clock,
  Star,
  AlertTriangle,
  BarChart3,
  Calendar,
  User,
  Zap,
  RefreshCw,
  Play,
  Pause
} from "lucide-react";
import { Link } from "react-router-dom";
import { useFeedbackCycles, useUserFeedbackResponses, useFeedbackAnalytics } from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";
import { CreateFeedbackModal } from "@/components/modals/CreateFeedbackModal";
import { EditFeedbackModal } from "@/components/modals/EditFeedbackModal";
import { DeleteFeedbackModal } from "@/components/modals/DeleteFeedbackModal";
import type { FeedbackResponseRecord } from "@/types";

type FeedbackCycleParticipant =
  | string
  | {
      id?: string | null
      userId?: string | null
      user?: {
        id?: string | null
      } | null
    }
  | null;

type FeedbackCycle = {
  id?: string
  cycleType?: string | null
  isRecurring?: boolean | null
  participants?: FeedbackCycleParticipant[] | null
  targetUserId?: string | null
  targetUser?: {
    id?: string | null
  } | null
};

type FeedbackAnalyticsMetrics = {
  overallRating?: number | null
  totalReviews?: number | null
  ratingsCount?: number | null
  totalFeedbackReceived?: number | null
  totalFeedbackGiven?: number | null
  contributors?: number | null
  pendingReviews?: number | null
};

type FeedbackAnalyticsData = {
  metrics?: FeedbackAnalyticsMetrics | null
  strengths?: Array<{ name?: string | null; score?: number | null; rating?: number | null }> | null
  developmentAreas?: Array<{ name?: string | null; score?: number | null; rating?: number | null }> | null
};

type FormattedFeedbackItem = {
  id: string
  title: string
  content: string
  category: string
  rating?: number
  author: {
    name: string
    avatar: string | null
  }
  recipient: {
    name: string
    avatar: string | null
  }
  createdAt: Date
  feedbackType: 'targeted' | 'regular'
  cycleType?: string | null
  fromCycle?: string | null
  questionText: string
  isAnonymous: boolean
  respondentId?: string | null
  targetId?: string | null
};

type ModalFeedback = {
  id: string
  responseText: string
  responseRating?: number
  isAnonymous: boolean
  submittedAt: string
  feedbackCycle?: {
    id: string
    name: string
  }
};

export function FeedbackPage() {
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isQuickModalOpen, setIsQuickModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<ModalFeedback | null>(null);

  // Use API hooks for real data
  const { user } = useAuth();
  const { data: feedbackCyclesData, isLoading: cyclesLoading, error: cyclesError } = useFeedbackCycles();
  const { data: feedbackResponsesData, isLoading: responsesLoading } = useUserFeedbackResponses();
  const { data: analyticsData, isLoading: analyticsLoading } = useFeedbackAnalytics();

  const currentUserId = user?.id;
  const feedbackCycles: FeedbackCycle[] = useMemo(() => {
    if (Array.isArray(feedbackCyclesData?.data)) {
      return feedbackCyclesData.data as FeedbackCycle[];
    }
    return [];
  }, [feedbackCyclesData]);

  const feedbackResponses: FeedbackResponseRecord[] = useMemo(() => {
    if (Array.isArray(feedbackResponsesData?.data)) {
      return feedbackResponsesData.data as FeedbackResponseRecord[];
    }
    return [];
  }, [feedbackResponsesData]);

  const analytics: FeedbackAnalyticsData | null = useMemo(() => {
    if (analyticsData?.data && typeof analyticsData.data === 'object') {
      return analyticsData.data as FeedbackAnalyticsData;
    }
    return null;
  }, [analyticsData]);

  const isCycleParticipant = (cycle: any) => {
    if (!currentUserId || !Array.isArray(cycle?.participants)) {
      return false;
    }

    return cycle.participants.some((participant: any) => {
      if (!participant) return false;
      if (typeof participant === 'string') {
        return participant === currentUserId;
      }

      if (typeof participant === 'object') {
        if (participant.userId && participant.userId === currentUserId) {
          return true;
        }
        if (participant.user?.id && participant.user.id === currentUserId) {
          return true;
        }
        if (participant.id && participant.id === currentUserId) {
          return true;
        }
      }

      return false;
    });
  };

  const targetedFeedbackCycles = feedbackCycles.filter((cycle) => {
    const targetId = cycle?.targetUserId || cycle?.targetUser?.id;
    return targetId === currentUserId || isCycleParticipant(cycle);
  });

  const recurringFeedbackCycles = feedbackCycles.filter((cycle) => {
    const cycleType = typeof cycle?.cycleType === 'string' ? cycle.cycleType.toLowerCase() : cycle?.cycleType;
    const isRecurringType = cycle?.isRecurring || cycleType === 'recurring';
    const targetId = cycle?.targetUserId || cycle?.targetUser?.id;
    return isRecurringType && (targetId === currentUserId || isCycleParticipant(cycle));
  });

  const handleFeedbackRequest = (requestData: unknown) => {
    console.log("Feedback request sent:", requestData);
    // Here you would typically send the request to your backend API
  };

  const handleQuickRequest = (requestData: unknown) => {
    console.log("Quick feedback request sent:", requestData);
    // Here you would typically send the quick request to your backend API
  };

  const mapToModalFeedback = (feedback: FeedbackResponseRecord): ModalFeedback => ({
    id: feedback.id,
    responseText: feedback.responseText ?? '',
    responseRating: typeof feedback.responseRating === 'number' ? feedback.responseRating : undefined,
    isAnonymous: feedback.isAnonymous,
    submittedAt: feedback.submittedAt ?? feedback.createdAt ?? new Date().toISOString(),
    feedbackCycle: feedback.cycle
      ? {
          id: feedback.cycle.id ?? feedback.id,
          name: feedback.cycle.name ?? 'Feedback Cycle',
        }
      : undefined,
  });

  const handleEditFeedback = (feedback: FeedbackResponseRecord) => {
    setSelectedFeedback(mapToModalFeedback(feedback));
    setIsEditModalOpen(true);
  };

  const handleDeleteFeedback = (feedback: FeedbackResponseRecord) => {
    setSelectedFeedback(mapToModalFeedback(feedback));
    setIsDeleteModalOpen(true);
  };

  const handleModalSuccess = () => {
    setIsRequestModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedFeedback(null);
  };

  // Get feedback items from API responses - only show submitted feedback
  const feedbackItems = feedbackResponses.filter((response) =>
    (response.target?.id === currentUserId || response.targetId === currentUserId) &&
    response.submittedAt
  );

  // Transform API data to match UI expectations
  const formattedFeedbackItems: FormattedFeedbackItem[] = feedbackItems.map((response) => {
    const ratingValue = response.responseRating;
    const normalizedRating = typeof ratingValue === 'number'
      ? ratingValue
      : ratingValue !== null && ratingValue !== undefined
        ? Number(ratingValue)
        : undefined;

    const respondentName = `${response.respondent?.firstName || ''} ${response.respondent?.lastName || ''}`.trim();
    const targetName = `${response.target?.firstName || ''} ${response.target?.lastName || ''}`.trim();

    return {
      id: response.id,
      title: response.cycle?.name || "Feedback",
      content: response.responseText || "No text response provided",
      category: response.question?.text?.split(' ')[0] || "General",
      rating: Number.isFinite(normalizedRating) ? normalizedRating : undefined,
      author: {
        name: response.isAnonymous ? "Anonymous" : respondentName || "Unknown",
        avatar: response.respondent?.avatar || null,
      },
      recipient: {
        name: targetName || "Unknown",
        avatar: response.target?.avatar || null,
      },
      createdAt: new Date(response.submittedAt ?? response.createdAt ?? Date.now()),
      feedbackType: response.cycle?.cycleType === "targeted" ? "targeted" : "regular",
      cycleType: response.cycle?.cycleType || "regular",
      fromCycle: response.cycle?.name || "Unknown Cycle",
      questionText: response.question?.text || "General Feedback",
      isAnonymous: response.isAnonymous || false,
      respondentId: response.respondent?.id,
      targetId: response.target?.id || response.targetId,
    };
  });


  // Show loading state
  if (cyclesLoading || responsesLoading || analyticsLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Feedback</h1>
            <p className="text-muted-foreground">Loading your feedback data...</p>
          </div>
        </div>
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-4 bg-muted rounded w-full mb-2"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Show error state
  if (cyclesError) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-destructive mb-2">Failed to load feedback</h2>
          <p className="text-muted-foreground mb-4">{cyclesError.message}</p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Use real analytics data or fallback to default values
  const strengths = analytics?.strengths || [
    { name: "No feedback data yet", score: 0, rating: 0 },
  ];

  const developmentAreas = analytics?.developmentAreas || [
    { name: "No feedback data yet", score: 0, rating: 0 },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-8 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-border pb-6">
        <div>
          <h1 className="text-4xl font-bold text-foreground tracking-tight mb-2">My Feedback Journey</h1>
          <p className="text-xl text-muted-foreground">Discover your strengths, growth areas, and professional insights</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setIsRequestModalOpen(true)}
            className="rounded-xl"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Give Feedback
          </Button>
          <Link to="/feedback/analytics">
            <Button variant="outline" className="rounded-xl">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </Button>
          </Link>
        </div>
      </div>

      {/* Reputation Overview */}
      <div className="bg-card border border-border shadow-sm p-8 rounded-3xl">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-center">
          {/* Overall Rating */}
          <div className="text-center lg:col-span-1">
            <div className="w-28 h-28 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
              <span className="text-4xl font-bold text-white">
                {analytics?.metrics?.overallRating || 0}
              </span>
            </div>
            <div className="text-2xl font-bold text-foreground mb-2">Overall Rating</div>
            <div className="text-muted-foreground">
              Based on {analytics?.metrics?.totalReviews || 0} feedback responses
              {analytics?.metrics?.ratingsCount && analytics?.metrics?.ratingsCount < (analytics?.metrics?.totalReviews || 0) &&
                <span className="block text-xs mt-1">
                  ({analytics.metrics.ratingsCount} with ratings)
                </span>
              }
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 lg:col-span-3">
            <Link to="/feedback/total" className="text-center group block hover:bg-muted/50 rounded-lg p-3 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:shadow-sm transition-all">
                <BarChart3 className="h-6 w-6 text-slate-600" />
              </div>
              <div className="text-2xl font-bold text-foreground mb-1">
                {(analytics?.metrics?.totalFeedbackReceived || 0) + (analytics?.metrics?.totalFeedbackGiven || 0)}
              </div>
              <div className="text-muted-foreground font-medium text-sm">Total</div>
            </Link>
            <Link to="/feedback/total/received" className="text-center group block hover:bg-muted/50 rounded-lg p-3 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:shadow-sm transition-all">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-foreground mb-1">
                {analytics?.metrics?.totalFeedbackReceived || 0}
              </div>
              <div className="text-muted-foreground font-medium text-sm">Received</div>
            </Link>
            <Link to="/feedback/total/given" className="text-center group block hover:bg-muted/50 rounded-lg p-3 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:shadow-sm transition-all">
                <MessageSquare className="h-6 w-6 text-purple-600" />
              </div>
              <div className="text-2xl font-bold text-foreground mb-1">
                {analytics?.metrics?.totalFeedbackGiven || 0}
              </div>
              <div className="text-muted-foreground font-medium text-sm">Given</div>
            </Link>
            <Link to="/feedback/contributors" className="text-center group block hover:bg-muted/50 rounded-lg p-3 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:shadow-sm transition-all">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-foreground mb-1">
                {analytics?.metrics?.contributors || 0}
              </div>
              <div className="text-muted-foreground font-medium text-sm">Contributors</div>
            </Link>
            <Link to="/feedback/pending" className="text-center group block hover:bg-muted/50 rounded-lg p-3 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:shadow-sm transition-all">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="text-2xl font-bold text-foreground mb-1">
                {analytics?.metrics?.pendingReviews || 0}
              </div>
              <div className="text-muted-foreground font-medium text-sm">Pending</div>
            </Link>
          </div>
        </div>
      </div>

      {/* Strengths & Development Areas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Strengths */}
        <div className="bg-card border border-border shadow-sm p-8 rounded-3xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl flex items-center justify-center">
              <Star className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-foreground">Top Strengths</h3>
          </div>
          <div className="space-y-6">
            {strengths.map((strength, index) => {
              const name = strength?.name ?? `Strength ${index + 1}`;
              const ratingRaw = strength?.rating ?? null;
              const ratingValue = typeof ratingRaw === 'number' ? ratingRaw : null;
              const scoreRaw = Number(strength?.score ?? 0);
              const scoreValue = Number.isFinite(scoreRaw)
                ? Math.max(0, Math.min(100, scoreRaw))
                : 0;

              return (
                <div key={`${name}-${index}`} className="group">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-foreground font-medium">{name}</span>
                    <span className="text-xl font-bold text-foreground">
                      {ratingValue !== null && ratingValue > 0 ? ratingValue : '--'}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all group-hover:shadow-sm"
                      style={{ width: `${scoreValue}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Development Areas */}
        <div className="bg-card border border-border shadow-sm p-8 rounded-3xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="text-2xl font-bold text-foreground">Growth Areas</h3>
          </div>
          <div className="space-y-6">
            {developmentAreas.map((area, index) => {
              const name = area?.name ?? `Area ${index + 1}`;
              const ratingRaw = area?.rating ?? null;
              const ratingValue = typeof ratingRaw === 'number' ? ratingRaw : null;
              const scoreRaw = Number(area?.score ?? 0);
              const scoreValue = Number.isFinite(scoreRaw)
                ? Math.max(0, Math.min(100, scoreRaw))
                : 0;

              return (
                <div key={`${name}-${index}`} className="group">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-foreground font-medium">{name}</span>
                    <span className="text-xl font-bold text-foreground">
                      {ratingValue !== null && ratingValue > 0 ? ratingValue : '--'}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-yellow-500 to-orange-500 h-3 rounded-full transition-all group-hover:shadow-sm"
                      style={{ width: `${scoreValue}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Feedback */}
      <div className="bg-card border border-border shadow-sm p-8 rounded-3xl">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl flex items-center justify-center">
              <MessageSquare className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-foreground">Recent Feedback</h3>
              <p className="text-muted-foreground">All feedback you've received, including targeted and recurring cycles</p>
            </div>
          </div>
          <Link to="/feedback/history">
            <Button variant="outline" className="rounded-xl">
              View All Feedback
            </Button>
          </Link>
        </div>

        <div className="space-y-6">
          {formattedFeedbackItems.length > 0 ? formattedFeedbackItems.slice(0, 5).map((item) => (
            <div key={item.id} className={`bg-card border-2 rounded-2xl p-6 hover:shadow-md transition-all group ${
              item.feedbackType === 'targeted'
                ? 'border-border hover:border-primary/50 bg-card'
                : 'border-border hover:border-border'
            }`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    item.feedbackType === 'targeted'
                      ? 'bg-gradient-to-br from-purple-50 to-pink-50'
                      : 'bg-gradient-to-br from-indigo-50 to-purple-50'
                  }`}>
                    {item.feedbackType === 'targeted' ? (
                      <Star className="h-6 w-6 text-primary" />
                    ) : (
                      <User className="h-6 w-6 text-indigo-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <div className="font-bold text-foreground text-lg">{item.title}</div>
                      {item.feedbackType === 'targeted' && (
                        <div className="flex items-center gap-1.5 bg-purple-100 text-purple-700 px-3 py-1 rounded-lg border border-purple-200">
                          {item.cycleType === 'recurring' && <RefreshCw className="h-3 w-3" />}
                          <span className="text-xs font-medium">
                            {item.cycleType === 'recurring' ? 'Recurring Feedback' : 'Targeted Feedback'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-muted-foreground">
                      From {item.author.name} • {item.createdAt.toLocaleDateString()}
                      {item.feedbackType === 'targeted' && item.fromCycle && (
                        <span className="ml-2 text-primary">• {item.fromCycle}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge
                    variant="outline"
                    className={`rounded-lg px-3 py-1 border ${
                      item.feedbackType === 'targeted'
                        ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-purple-200'
                        : 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200'
                    }`}
                  >
                    {item.category}
                  </Badge>
                  {item.rating && (
                    <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1 rounded-lg border border-yellow-200">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-bold text-yellow-700">{item.rating}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className={`p-4 rounded-xl ${
                item.feedbackType === 'targeted' ? 'bg-muted/50 border border-border' : 'bg-muted/30'
              }`}>
                <p className="text-muted-foreground leading-relaxed">
                  "{item.content}"
                </p>
              </div>
            </div>
          )) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-muted/30 to-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No feedback yet</h3>
              <p className="text-muted-foreground">You haven't received any feedback yet. Check back later!</p>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modals */}
      <CreateFeedbackModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        users={[]} // TODO: Pass actual users list from API
        onSuccess={handleModalSuccess}
      />

      <EditFeedbackModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        feedback={selectedFeedback}
        onSuccess={handleModalSuccess}
      />

      <DeleteFeedbackModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        feedback={selectedFeedback}
        onSuccess={handleModalSuccess}
      />
    </div>
    </div>
  );
}
